import store from 'app/store';
import Flex from 'components/common/Flex';
import ValidationErrors from 'components/utilities/ValidationErrors';
import segmentHelper from 'helpers/segment-helper';
import { useAuth } from 'hooks/useAuth';
import _ from 'lodash';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, Col, Form, Row, Spinner } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { refreshAllAccountInfo, refreshDataExportMonths } from 'stores/app';
import { setPreAuthEmail, setToken } from 'stores/authentication';
import c from 'util/const';
import api from 'util/api';
import logger from 'util/logger';

const INITIAL_FORM = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  password: '',
  address1: '',
  address2: '',
  address3: '',
  city: '',
  state: '',
  country: 'USA',
  postalCode: '',
  localTimezone: ''
};

const OrganizationInvitedUserSignup = () => {
  const [searchParams] = useSearchParams();
  const inviteTokenGuid = searchParams.get('token') || '';
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const auth = useAuth();

  const [status, setStatus] = useState('loading');
  const [inviteInfo, setInviteInfo] = useState(null);
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [validationErr, setValidationErr] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const timezoneOptions = useMemo(
    () =>
      _.map(c.timezones, t => ({
        label: `${t.name} (${t.zone})`,
        value: t.zone
      })),
    []
  );

  useEffect(() => {
    if (!inviteTokenGuid) {
      setStatus('invalid');
      setValidationErr(['Invalid sign up invitation.']);
      return;
    }

    api.OrganizationUserInvite.getInvite(inviteTokenGuid)
      .then(res => {
        setInviteInfo(res);
        setFormData(current => ({
          ...current,
          firstName: res?.FirstName || '',
          lastName: res?.LastName || '',
          email: res?.Email || ''
        }));
        setStatus('ready');
      })
      .catch(err => {
        logger.log('organization invite details failed', err);
        setStatus('invalid');
        setValidationErr(['Invalid sign up invitation.']);
      });
  }, [inviteTokenGuid]);

  const setFieldValue = key => e => {
    setFormData(current => ({
      ...current,
      [key]: e.target.value
    }));
  };

  const getValidationErrors = () => {
    const errors = [];

    if (!formData.firstName.trim()) errors.push('First name is required.');
    if (!formData.lastName.trim()) errors.push('Last name is required.');
    if (!formData.email.trim()) errors.push('Email is required.');
    if (!formData.phone.trim()) errors.push('Phone number is required.');
    if (!formData.password.trim()) {
      errors.push('Password is required.');
    } else if (formData.password.trim().length < 8) {
      errors.push('Password must have at least 8 characters.');
    }
    if (!formData.address1.trim()) errors.push('Address 1 is required.');
    if (!formData.city.trim()) errors.push('City is required.');
    if (!formData.state) errors.push('State is required.');
    if (!formData.country) errors.push('Country is required.');
    if (!formData.postalCode.trim()) errors.push('Postal code is required.');
    if (!formData.localTimezone) errors.push('Timezone is required.');

    return errors;
  };

  const completeAuthenticatedSignup = async signupRes => {
    let authToken = _.get(signupRes, 'Token');

    if (!authToken) {
      const loginRes = await api.Token.login(formData.email, formData.password);
      authToken = _.get(loginRes, 'Token');
    }

    if (!authToken) {
      throw new Error('Missing authentication token.');
    }

    await Promise.all([
      api.setToken(authToken),
      auth.setAuthToken(authToken),
      dispatch(setToken(authToken)),
      dispatch(setPreAuthEmail(formData.email)),
      dispatch(refreshDataExportMonths())
    ]);

    await dispatch(refreshAllAccountInfo());

    try {
      segmentHelper.identity();
      const user = store.getState().app.userMaster;
      if (user) {
        segmentHelper.track(segmentHelper.events.SIGNUP_SUCCESS, {
          name: `${user.FirstName} ${user.LastName}`,
          email: user.Email,
          createdAt: user.Created,
          id: user.GUID
        });

        toast.success(`Welcome, ${user.FirstName} ${user.LastName}`, {
          theme: 'colored',
          position: 'bottom-center'
        });
      }
    } catch (err) {
      logger.log('organization invited signup post-login handlers failed', err);
    }

    navigate('/', { replace: true });
  };

  const handleSubmit = async e => {
    e.preventDefault();

    const errors = getValidationErrors();
    if (errors.length > 0) {
      setValidationErr(errors);
      return;
    }

    setSubmitting(true);
    setValidationErr([]);

    try {
      const response = await api.OrganizationUserInvite.signup({
        Token: inviteTokenGuid,
        ContactPersonName:
          `${formData.firstName.trim()} ${formData.lastName.trim()}`.trim(),
        Email: formData.email.trim(),
        PhoneNumber: formData.phone.trim(),
        Password: formData.password,
        Address1: formData.address1.trim(),
        Address2: formData.address2.trim() || null,
        Address3: formData.address3.trim() || null,
        City: formData.city.trim(),
        State: formData.state,
        Province: null,
        Country: formData.country,
        PostalCode: formData.postalCode.trim(),
        LocalTimezone: formData.localTimezone
      });

      if (response?.Success === false) {
        throw response;
      }

      await completeAuthenticatedSignup(response);
    } catch (err) {
      logger.log('organization invited signup failed', err);
      setValidationErr([
        err?.Message ||
          err?.Text ||
          'Failed to complete signup. Please verify your invitation and try again.'
      ]);
    } finally {
      setSubmitting(false);
    }
  };

  if (status === 'loading') {
    return (
      <Flex justifyContent="center" className="py-5">
        <Spinner animation="border" size="sm" />
      </Flex>
    );
  }

  if (status === 'invalid') {
    return (
      <Card className="border-0 shadow-none p-4 mb-5">
        <h4 className="mb-3">Complete Your Signup</h4>
        <ValidationErrors errors={validationErr} />
        <Button as={Link} to="/login" variant="primary">
          Proceed to login
        </Button>
      </Card>
    );
  }

  const orgName = inviteInfo?.OrganizationName || 'this organization';
  const expiresAt = inviteInfo?.ExpiresAt
    ? moment.utc(inviteInfo.ExpiresAt).local().format('MMMM Do YYYY, h:mm a')
    : null;

  return (
    <Card className="border-0 shadow-none p-4 mb-5">
      <h4 className="mb-3">Complete Your Signup</h4>
      <p className="text-600">
        You have received the invitation to join {orgName}. Please fill details
        to complete sign up.
      </p>
      {expiresAt ? (
        <p className="text-500 fs--1">Invitation expires on {expiresAt}.</p>
      ) : null}

      <ValidationErrors errors={validationErr} />

      <Form onSubmit={handleSubmit}>
        <Row className="g-2">
          <Col sm={6}>
            <Form.Group className="mb-3" controlId="orgInviteFirstName">
              <Form.Label>First Name</Form.Label>
              <Form.Control
                value={formData.firstName}
                onChange={setFieldValue('firstName')}
                disabled={submitting}
              />
            </Form.Group>
          </Col>
          <Col sm={6}>
            <Form.Group className="mb-3" controlId="orgInviteLastName">
              <Form.Label>Last Name</Form.Label>
              <Form.Control
                value={formData.lastName}
                onChange={setFieldValue('lastName')}
                disabled={submitting}
              />
            </Form.Group>
          </Col>
        </Row>

        <Form.Group className="mb-3" controlId="orgInviteEmail">
          <Form.Label>Email</Form.Label>
          <Form.Control value={formData.email} disabled readOnly />
        </Form.Group>

        <Row className="g-2">
          <Col sm={6}>
            <Form.Group className="mb-3" controlId="orgInvitePhone">
              <Form.Label>Phone Number</Form.Label>
              <Form.Control
                type="tel"
                value={formData.phone}
                onChange={setFieldValue('phone')}
                disabled={submitting}
              />
            </Form.Group>
          </Col>
          <Col sm={6}>
            <Form.Group className="mb-3" controlId="orgInvitePassword">
              <Form.Label>Password</Form.Label>
              <Form.Control
                type="password"
                value={formData.password}
                onChange={setFieldValue('password')}
                disabled={submitting}
              />
            </Form.Group>
          </Col>
        </Row>

        <Form.Group className="mb-3" controlId="orgInviteAddress1">
          <Form.Label>Address 1</Form.Label>
          <Form.Control
            value={formData.address1}
            onChange={setFieldValue('address1')}
            disabled={submitting}
          />
        </Form.Group>

        <Row className="g-2">
          <Col sm={6}>
            <Form.Group className="mb-3" controlId="orgInviteAddress2">
              <Form.Label>Address 2</Form.Label>
              <Form.Control
                value={formData.address2}
                onChange={setFieldValue('address2')}
                disabled={submitting}
              />
            </Form.Group>
          </Col>
          <Col sm={6}>
            <Form.Group className="mb-3" controlId="orgInviteAddress3">
              <Form.Label>Address 3</Form.Label>
              <Form.Control
                value={formData.address3}
                onChange={setFieldValue('address3')}
                disabled={submitting}
              />
            </Form.Group>
          </Col>
        </Row>

        <Row className="g-2">
          <Col sm={6}>
            <Form.Group className="mb-3" controlId="orgInviteCity">
              <Form.Label>City</Form.Label>
              <Form.Control
                value={formData.city}
                onChange={setFieldValue('city')}
                disabled={submitting}
              />
            </Form.Group>
          </Col>
          <Col sm={6}>
            <Form.Group className="mb-3" controlId="orgInviteState">
              <Form.Label>State</Form.Label>
              <Form.Select
                value={formData.state}
                onChange={setFieldValue('state')}
                disabled={submitting}
              >
                <option value="">Select state</option>
                {c.states.map(option => (
                  <option key={option.text} value={option.text}>
                    {option.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>

        <Row className="g-2">
          <Col sm={6}>
            <Form.Group className="mb-3" controlId="orgInviteCountry">
              <Form.Label>Country</Form.Label>
              <Form.Select
                value={formData.country}
                onChange={setFieldValue('country')}
                disabled={submitting}
              >
                {c.country.map(option => (
                  <option key={option.text} value={option.text}>
                    {option.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col sm={6}>
            <Form.Group className="mb-3" controlId="orgInvitePostalCode">
              <Form.Label>Postal Code</Form.Label>
              <Form.Control
                value={formData.postalCode}
                onChange={setFieldValue('postalCode')}
                disabled={submitting}
              />
            </Form.Group>
          </Col>
        </Row>

        <Form.Group className="mb-4" controlId="orgInviteTimezone">
          <Form.Label>Timezone</Form.Label>
          <Form.Select
            value={formData.localTimezone}
            onChange={setFieldValue('localTimezone')}
            disabled={submitting}
          >
            <option value="">Select timezone</option>
            {timezoneOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Form.Select>
        </Form.Group>

        <div className="d-flex justify-content-between align-items-center">
          <Link to="/login" className="fs--1">
            Proceed to login
          </Link>
          <Button type="submit" variant="primary" disabled={submitting}>
            {submitting ? (
              <Spinner animation="border" size="sm" className="me-2" />
            ) : null}
            Complete Sign Up
          </Button>
        </div>
      </Form>
    </Card>
  );
};

export default OrganizationInvitedUserSignup;
